import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { modeloSchema } from './validation/modeloSchema'
import { ModeloDtoAlteracao, ModeloDtoCreate, ModeloView } from '../../core/_models'
import { createModelo, updateModelo } from '../../core/_requests'
import {
  Button,
  FetchState,
  FormFileInput,
  FormInput,
  FormSelect,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from 'app/components'
import { ButtonType, OptionsHelper, showAlert, showError } from '_metronic/helpers'

export const ModeloModal: FC = () => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: modelo } = (fetchStates['modelo'] || {}) as FetchState<ModeloView>

  const initialValues = {
    exist: !!modelo,
    ativo: modelo
      ? { label: modelo.ativo ? 'Ativo' : 'Inativo', value: modelo.ativo }
      : (undefined as IOption<boolean> | undefined),
    descricao: modelo?.descricao || '',
    titulo: modelo?.titulo || '',
    pdf: undefined as undefined | File,
    tipo: modelo?.tipoDocumento
      ? { label: modelo.tipoDocumento.descricao, value: modelo.tipoDocumento.id }
      : (undefined as IOption<number> | undefined),
  }

  const formik = useFormik({
    initialValues,
    validationSchema: useMemo(() => modeloSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (modelo) {
          const data: ModeloDtoAlteracao = {
            titulo: values.titulo,
            descricao: values.descricao,
            ativo: values.ativo?.value as boolean,
            tipo: values.tipo?.value as number,
          }
          await updateModelo(modelo.id as number, data)
        } else {
          const data: ModeloDtoCreate = {
            titulo: values.titulo,
            descricao: values.descricao,
            tipo: values.tipo?.value as number,
          }
          await createModelo(data, values.pdf as File)
        }
        await showAlert({
          title: t('common.success'),
          text: t(modelo ? 'alert.update' : 'alert.create', {
            name: t('modelo.label'),
            gender: t('modelo.gender'),
          }),
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormInput formik={formik} entity='titulo' name='titulo' className='mb-3' isRequired />
      <FormInput formik={formik} entity='descricao' name='descricao' className='mb-3' />
      <FormSelect
        formik={formik}
        entity='tipoDocumento'
        name='tipo'
        fetchOptions={OptionsHelper.getOptionsTiposAnexo}
        className='mb-3'
        isDisabled={formik.isSubmitting}
        isRequired
      />
      {modelo ? (
        <FormSelect
          formik={formik}
          entity='situacao'
          name='ativo'
          options={OptionsHelper.statusCadastro}
          isClearable={false}
          className='mb-3'
          isDisabled={formik.isSubmitting}
          isRequired
        />
      ) : (
        <FormFileInput
          formik={formik}
          entity='arquivo'
          name='pdf'
          accept='application/pdf'
          className='mb-3'
          isRequired
        />
      )}
      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>

        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
